import { useState, useRef, useCallback, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Sparkles, Loader2, X, ShoppingCart, RotateCcw, Upload, LogIn } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useFeatureSettings } from "@/hooks/useFeatureSettings";
import { trackAnalyticsEvent } from "@/hooks/useAnalytics";

interface AISkinConsultantProps {
  open: boolean;
  onClose: () => void;
}

interface SkinAnalysis {
  skin_type: string;
  concerns: string[];
  overall_condition: string;
}

interface ProductRecommendation {
  product_id: string;
  product_slug: string;
  product_name: string;
  product_brand: string;
  product_price: number;
  reason: string;
}

interface AnalysisResult {
  skin_analysis: SkinAnalysis;
  recommendations: ProductRecommendation[];
  general_advice: string;
}

export const AISkinConsultant = ({ open, onClose }: AISkinConsultantProps) => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { data: featureSettings } = useFeatureSettings();
  const navigate = useNavigate();
  const [step, setStep] = useState<"capture" | "analyzing" | "results">("capture");
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const requireLogin = featureSettings?.ai_skin_consultant_require_login ?? true;
  const rateLimitSeconds = featureSettings?.ai_skin_consultant_rate_limit_seconds ?? 60;
  const dailyLimit = featureSettings?.ai_skin_consultant_daily_limit ?? 5;

  // Use refs to avoid stale closures in callbacks
  const userRef = useRef(user);
  const requireLoginRef = useRef(requireLogin);
  const languageRef = useRef(language);

  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { requireLoginRef.current = requireLogin; }, [requireLogin]);
  useEffect(() => { languageRef.current = language; }, [language]);

  const t = {
    title: language === "lt" ? "AI Odos Konsultantė" : "AI Skin Consultant",
    subtitle: language === "lt" ? "Nufotografuokite savo odą ir gaukite personalizuotas rekomendacijas" : "Take a photo of your skin and get personalized recommendations",
    takePhoto: language === "lt" ? "Fotografuoti" : "Take Photo",
    uploadPhoto: language === "lt" ? "Įkelti nuotrauką" : "Upload Photo",
    analyzing: language === "lt" ? "Analizuojame jūsų odą..." : "Analyzing your skin...",
    analyzingHint: language === "lt" ? "AI konsultantė vertina odos būklę ir parenka geriausius produktus" : "AI consultant is evaluating skin condition and selecting best products",
    results: language === "lt" ? "Jūsų rezultatai" : "Your Results",
    skinType: language === "lt" ? "Odos tipas" : "Skin Type",
    concerns: language === "lt" ? "Pastebėtos problemos" : "Detected Concerns",
    condition: language === "lt" ? "Bendra būklė" : "Overall Condition",
    recommended: language === "lt" ? "Rekomenduojami produktai" : "Recommended Products",
    why: language === "lt" ? "Kodėl tinka" : "Why it fits",
    viewProduct: language === "lt" ? "Peržiūrėti" : "View Product",
    advice: language === "lt" ? "Bendri patarimai" : "General Advice",
    tryAgain: language === "lt" ? "Bandyti dar kartą" : "Try Again",
    startCamera: language === "lt" ? "Pradėti kamerą" : "Start Camera",
    cameraHint: language === "lt" ? "Nufotografuokite veidą arba odos sritį aiškioje šviesoje" : "Photograph your face or skin area in clear light",
    orUpload: language === "lt" ? "arba įkelkite nuotrauką" : "or upload a photo",
    error: language === "lt" ? "Nepavyko atlikti analizės. Bandykite dar kartą." : "Analysis failed. Please try again.",
    loginRequired: language === "lt" ? "Prisijunkite, kad galėtumėte naudoti AI konsultantę" : "Please log in to use the AI consultant",
    loginButton: language === "lt" ? "Prisijungti" : "Log In",
    rateLimited: language === "lt" 
      ? `Galite naudoti AI konsultantę kartą per ${rateLimitSeconds} sek. Bandykite vėliau.` 
      : `You can use the AI consultant once every ${rateLimitSeconds} sec. Please try again later.`,
    dailyLimitReached: language === "lt"
      ? `Pasiektas dienos limitas (${dailyLimit} užklausų per 24h). Bandykite vėliau.`
      : `Daily limit reached (${dailyLimit} requests per 24h). Please try again later.`,
  };

  const startCamera = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error(language === "lt" ? "Kamera nepalaikoma šiame įrenginyje. Įkelkite nuotrauką." : "Camera not supported on this device. Please upload a photo.");
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      // Set streaming first so the video element renders, then useEffect will attach the stream
      setIsStreaming(true);
    } catch (error) {
      console.error("Camera error:", error);
      toast.error(language === "lt" ? "Nepavyko pasiekti kameros. Įkelkite nuotrauką." : "Could not access camera. Please upload a photo.");
    }
  }, [language]);

  // Attach the stream to the video element AFTER it renders
  useEffect(() => {
    if (isStreaming && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(console.error);
    }
  }, [isStreaming]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
    setCapturedImage(dataUrl);
    stopCamera();
    analyzeImage(dataUrl);
  }, [stopCamera]);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      setCapturedImage(dataUrl);
      analyzeImage(dataUrl);
    };
    reader.readAsDataURL(file);
  }, []);

  // Rate limiting is now handled server-side per user

  const analyzeImage = async (dataUrl: string) => {
    // Check login requirement using refs for latest values
    if (requireLoginRef.current && !userRef.current) {
      toast.error(languageRef.current === "lt" ? "Prisijunkite, kad galėtumėte naudoti AI konsultantę" : "Please log in to use the AI consultant");
      resetState();
      return;
    }

    setStep("analyzing");
    trackAnalyticsEvent('ai_consultant', { action: 'analyze' });
    try {
      const base64 = dataUrl.split(",")[1];
      // Extract media type from data URL (e.g., "data:image/png;base64,...")
      const mediaTypeMatch = dataUrl.match(/^data:(image\/[a-zA-Z+]+);base64,/);
      const mediaType = mediaTypeMatch?.[1] || "image/jpeg";
      const { data, error } = await supabase.functions.invoke("ai-skin-consultant", {
        body: { imageBase64: base64, language, mediaType },
      });

      if (error) throw error;
      
        if (data?.error) {
        if (data.error === "RATE_LIMITED") {
          const waitSec = data.wait_seconds || rateLimitSeconds;
          const msg = language === "lt" 
            ? `Galite naudoti AI konsultantę kartą per ${waitSec} sek. Bandykite vėliau.`
            : `You can use the AI consultant once every ${waitSec} sec. Please try again later.`;
          toast.error(msg);
          resetState();
          return;
        }
        if (data.error === "DAILY_LIMIT_REACHED") {
          toast.error(t.dailyLimitReached);
          resetState();
          return;
        }
        if (data.error === "LOGIN_REQUIRED") {
          toast.error(t.loginRequired);
          resetState();
          return;
        }
        throw new Error(data.error);
      }

      setResult(data as AnalysisResult);
      setStep("results");
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error(t.error);
      resetState();
    }
  };

  const resetState = () => {
    setCapturedImage(null);
    setResult(null);
    setStep("capture");
    stopCamera();
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  const handleViewProduct = (slug: string) => {
    onClose();
    navigate(`/produktas/${slug}`);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto p-0">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-accent/10 p-6 pb-4 rounded-t-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <div className="p-2 rounded-full bg-primary/10">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              {t.title}
            </DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">{t.subtitle}</p>
          </DialogHeader>
        </div>

        <div className="p-6 pt-2">
          {/* CAPTURE STEP */}
          {step === "capture" && requireLogin && !user ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="p-4 rounded-full bg-primary/10">
                <LogIn className="h-8 w-8 text-primary" />
              </div>
              <p className="text-center text-muted-foreground">{t.loginRequired}</p>
              <Button onClick={() => { handleClose(); navigate('/auth'); }}>
                <LogIn className="h-4 w-4 mr-2" />
                {t.loginButton}
              </Button>
            </div>
          ) : step === "capture" && (
            <div className="space-y-4">
              {isStreaming ? (
                <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]">
                  <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline 
                    muted 
                    // @ts-ignore - webkit attribute for iOS
                    webkit-playsinline="true"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-48 h-64 border-2 border-white/40 rounded-full" />
                  </div>
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                    <Button
                      size="lg"
                      onClick={capturePhoto}
                      className="rounded-full w-16 h-16 bg-white hover:bg-white/90 shadow-lg"
                    >
                      <Camera className="h-7 w-7 text-primary" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <button
                    onClick={startCamera}
                    className="w-full aspect-[4/3] rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 hover:bg-primary/10 transition-all flex flex-col items-center justify-center gap-3 cursor-pointer group"
                  >
                    <div className="p-4 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                      <Camera className="h-8 w-8 text-primary" />
                    </div>
                    <span className="font-medium text-primary">{t.startCamera}</span>
                    <span className="text-xs text-muted-foreground max-w-[250px] text-center">{t.cameraHint}</span>
                  </button>

                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-xs text-muted-foreground">{t.orUpload}</span>
                    <div className="h-px flex-1 bg-border" />
                  </div>

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {t.uploadPhoto}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                </div>
              )}
              <canvas ref={canvasRef} className="hidden" />
            </div>
          )}

          {/* ANALYZING STEP */}
          {step === "analyzing" && (
            <div className="flex flex-col items-center justify-center py-12 space-y-6">
              {capturedImage && (
                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-primary/20 shadow-lg">
                  <img src={capturedImage} alt="Captured" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="relative">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <Sparkles className="h-4 w-4 text-primary absolute -top-1 -right-1 animate-pulse" />
              </div>
              <div className="text-center space-y-1">
                <p className="font-semibold text-foreground">{t.analyzing}</p>
                <p className="text-sm text-muted-foreground">{t.analyzingHint}</p>
              </div>
            </div>
          )}

          {/* RESULTS STEP */}
          {step === "results" && result && (
            <div className="space-y-5">
              {/* Skin Analysis Card */}
              <div className="rounded-xl bg-gradient-to-br from-primary/5 to-accent/5 p-4 space-y-3">
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  {t.results}
                </h3>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t.skinType}:</span>
                    <span className="font-medium text-foreground">{result.skin_analysis.skin_type}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t.concerns}:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {result.skin_analysis.concerns.map((c, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t.condition}:</span>
                    <span className="font-medium text-foreground">{result.skin_analysis.overall_condition}</span>
                  </div>
                </div>
              </div>

              {/* Product Recommendations */}
              <div className="space-y-3">
                <h3 className="font-semibold text-foreground">{t.recommended}</h3>
                {result.recommendations.map((rec, i) => (
                  <div key={i} className="rounded-xl border border-border p-4 space-y-2 hover:border-primary/30 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-foreground text-sm">{rec.product_name}</p>
                        <p className="text-xs text-muted-foreground">{rec.product_brand}</p>
                      </div>
                      <span className="font-bold text-primary whitespace-nowrap">€{rec.product_price.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      <span className="font-medium text-foreground">{t.why}: </span>{rec.reason}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full text-xs"
                      onClick={() => handleViewProduct(rec.product_slug)}
                    >
                      <ShoppingCart className="h-3 w-3 mr-1" />
                      {t.viewProduct}
                    </Button>
                  </div>
                ))}
              </div>

              {/* General Advice */}
              {result.general_advice && (
                <div className="rounded-xl bg-muted/50 p-4">
                  <h3 className="font-semibold text-foreground text-sm mb-2">{t.advice}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{result.general_advice}</p>
                </div>
              )}

              {/* Try Again */}
              <Button variant="outline" className="w-full" onClick={resetState}>
                <RotateCcw className="h-4 w-4 mr-2" />
                {t.tryAgain}
              </Button>

              
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
